import { 
  ArrowLeft, Plane, Wifi, Zap, Ruler, Luggage, ShieldCheck, 
  XCircle, Clock, MapPin, CreditCard, ChevronRight, Info 
} from 'lucide-react';

import airplane_seat from "../../../../icons/airplane_seat.png"
import { markup } from "../../../../helpers/Prices";
import { 
    convert24HTimeToAMPM, 
    get_short_date_MMMDD, 
    get_currency_symbol, 
    get_short_date_DAYMMMDD,
    add_commas_to_number,
    BubbleSort,
} from "../../../../helpers/general";
import { getTotalBags } from "../../../../helpers/FlightsFilterHelpers";

import SelectedTicketItinSegments from "../SelectedTicketItinSegments";

const Type4FullFlightDetailsCard = (props) => {

    const {
        rawData,
        data_provider,
        bookingEngine,
        hasNewMessageFromParent,
        currentParentMessge,
        PriceMarkupValue,
        canShowPrice,
        unselectFlightOffer,
        begin_checkout,
        flight,
    } = props;

    const offer = flight?.data;

  const mainSlice = offer.slices[0];
  const firstSegment = mainSlice.segments[0];
  const cabinInfo = firstSegment.passengers[0].cabin;
  const amenities = cabinInfo?.amenities;

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
      fontFamily: "'Inter', sans-serif",
      backgroundColor: "rgb(248, 250, 252)",
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '2rem',
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '1.5rem',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f1f5f9',
      marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: 900,
      color: '#0f172a',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1.5rem',
    },
    badge: {
      fontSize: '0.75rem',
      fontWeight: 800,
      padding: '0.4rem 0.8rem',
      borderRadius: '9999px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    }
  };

  const formatDuration = (isoDuration) => {
    // Simple parser for ISO 8601 duration like "PT7H58M"
    const hoursMatch = isoDuration.match(/(\d+)H/);
    const minsMatch = isoDuration.match(/(\d+)M/);
    
    const hours = hoursMatch ? hoursMatch[1] : '0';
    const mins = minsMatch ? minsMatch[1] : '0';
    
    return `${hours}h ${mins}m`;
  };

  const formatTime = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(parseFloat(amount));
  };

    const book_item_onclick = () => {
        begin_checkout(offer, rawData, data_provider);
        setTimeout(()=>{
            global?.__unselectFlightOffer();
            global?.hide_selected_ticket_details_pane();
        }, 300);
    }
    window.__Selected_Ticket_Book_Item_Onclick = book_item_onclick;

  return (
    <div style={styles.container}>
      {/* Back Button & Title */}
      <div style={styles.header}>
        <button 
          onClick={()=>{global?.hide_selected_ticket_details_pane(); unselectFlightOffer();}}
          className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors group"
        >
          <div className="w-8 h-8 rounded-full --bg-slate-100 flex items-center justify-center group-hover:bg-indigo-50">
            <i className='fa-solid fa-arrow-left'></i>
          </div>
          Back to Search
        </button>
        <div className="text-right">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Price</p>
          <p className="text-3xl font-black text-slate-900">{formatCurrency(offer.total_amount, offer.total_currency)}</p>
        </div>
      </div>

      <div className="--grid --grid-cols-1 lg:grid-cols-3 --gap-8">
        {/* Left Column: Itinerary & Amenities */}
        <div className="lg:col-span-2">
          {/* Main Journey Card */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>
              <div style={{background: (bookingEngine?.actionButtonsBg+"26")}} className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600">
                <Plane style={{ width: "16px", height: "16px" }} />
              </div>
              Flight Itinerary
            </div>

            <div className="space-y-0 relative pl-4">
              <div className="absolute left-6 top-2 bottom-2 w-0.5 bg-indigo-100"></div>
              
              {mainSlice.segments.map((segment, idx) => (
                <div key={segment.id} className="relative pl-12 pb-8 last:pb-0">
                  <div className="absolute left-[-11px] top-1.5 w-[14px] h-[14px] rounded-full bg-white border-2 border-indigo-600 z-10 shadow-sm"></div>
                  
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-black text-slate-900">{formatTime(segment.departing_at)}</span>
                        <span className="text-sm font-bold text-slate-500">{segment.origin.city_name} • {segment.origin.name} ({segment.origin.iata_code})</span>
                      </div>
                      {segment.origin_terminal && (
                        <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-2 py-0.5 rounded uppercase">Terminal {segment.origin_terminal}</span>
                      )}
                    </div>
                  </div>

                  <div className="my-6 p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-4">
                    <img src={segment.marketing_carrier.logo_symbol_url} className="w-10 h-10 object-contain bg-white p-1 rounded-lg" alt="" />
                    <div>
                      <p className="text-sm font-bold text-slate-800">{segment.marketing_carrier.name}</p>
                      <p className="text-xs font-medium text-slate-400">Flight {segment.marketing_carrier_flight_number} • {segment.aircraft?.name || 'Standard Aircraft'}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-1 text-[10px] font-black text-indigo-400 uppercase">
                      <Clock style={{ width: "16px", height: "16px" }} /> {formatDuration(segment.duration)}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-black text-slate-900">{formatTime(segment.arriving_at)}</span>
                      <span className="text-sm font-bold text-slate-500">{segment.destination.city_name} • {segment.destination.name} ({segment.destination.iata_code})</span>
                    </div>
                    {segment.destination_terminal && (
                      <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-2 py-0.5 rounded uppercase">Terminal {segment.destination_terminal}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cabin & Amenities Card */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>
              <div style={{background: (bookingEngine?.actionButtonsBg+"26")}} className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600">
                <Zap style={{ width: "16px", height: "16px" }} />
              </div>
              Cabin Experience ({cabinInfo?.marketing_name})
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div style={{background: (bookingEngine?.actionButtonsBg+"26")}} className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                  <i style={{color: bookingEngine?.actionButtonsBg}} className='fa-solid fa-wifi'></i>
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase">WiFi</p>
                  <p className="text-sm font-bold text-slate-800">
                    {amenities?.wifi?.available ? (amenities.wifi.cost === 'paid' ? 'Available (Paid)' : 'Free WiFi') : 'Not available'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div style={{background: (bookingEngine?.actionButtonsBg+"26")}} className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                  <i style={{color: bookingEngine?.actionButtonsBg}} className='fa-solid fa-ruler'></i>
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase">Seat Pitch</p>
                  <p className="text-sm font-bold text-slate-800">{amenities?.seat?.pitch || '29'}" Standard</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div style={{background: (bookingEngine?.actionButtonsBg+"26")}} className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                  <i style={{color: bookingEngine?.actionButtonsBg}} className='fa-solid fa-plug-circle-bolt'></i>
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase">Power</p>
                  <p className="text-sm font-bold text-slate-800">{amenities?.power?.available ? 'USB & AC Outlets' : 'No power'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Policies & Add-ons */}
        <div className="space-y-6">
          {/* Booking Summary */}
          <div style={{...styles.card, background: '#1e293b', color: '#fff', border: 'none'}}>
            <h4 className="text-sm font-black uppercase tracking-widest mb-6 flex items-center gap-2">
              <CreditCard style={{ width: "16px", height: "16px" }} className="text-indigo-400" />
              Fare Rules
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2">
                  {offer.conditions?.change_before_departure?.allowed ? (
                    <ShieldCheck style={{ width: "16px", height: "16px" }} className="text-green-400" />
                  ) : (
                    <XCircle style={{ width: "16px", height: "16px" }} className="text-red-400" />
                  )}
                  <span className="text-sm font-bold">Changes</span>
                </div>
                <span className="text-xs font-medium text-slate-400">
                  {offer.conditions?.change_before_departure?.allowed 
                    ? `Allowed (Fee: ${formatCurrency(offer.conditions.change_before_departure.penalty_amount, offer.total_currency)})` 
                    : 'Not allowed'}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2">
                  {offer.conditions?.refund_before_departure?.allowed ? (
                    <ShieldCheck style={{ width: "16px", height: "16px" }} className="text-green-400" />
                  ) : (
                    <XCircle style={{ width: "16px", height: "16px" }} className="text-red-400" />
                  )}
                  <span className="text-sm font-bold">Refunds</span>
                </div>
                <span className="text-xs font-medium text-slate-400">
                  {offer.conditions?.refund_before_departure?.allowed ? 'Allowed' : 'Non-refundable'}
                </span>
              </div>
            </div>

            {offer.payment_requirements && (
              <div className="mt-8 p-4 bg-indigo-500/20 rounded-xl border border-indigo-400/30">
                <div className="flex items-center gap-2 mb-2">
                  <Clock style={{ width: "16px", height: "16px" }} className="text-indigo-300" />
                  <span className="text-xs font-black uppercase">Price Guarantee</span>
                </div>
                <p className="text-[11px] text-indigo-100 leading-relaxed">
                  Price guaranteed until <br/>
                  <span className="font-bold text-white">{new Date(offer.payment_requirements.price_guarantee_expires_at).toLocaleString()}</span>
                </p>
              </div>
            )}
          </div>

          {/* Baggage Services */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>
              <Luggage style={{ width: "16px", height: "16px" }} className="text-indigo-600" />
              Baggage
            </div>
            
            <div className="space-y-4">
              <p className="text-xs font-bold text-slate-400 uppercase mb-2">Included</p>
              {firstSegment.passengers[0].baggages.map((bag, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 capitalize font-medium">{bag.type.replace('_', ' ')}</span>
                  <span className="font-bold text-slate-900">{bag.quantity} included</span>
                </div>
              ))}

              {offer.available_services && offer.available_services.length > 0 && (
                <div className="pt-4 mt-4 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-3">Available Add-ons</p>
                  {offer.available_services.map(service => (
                    <div key={service.id} className="p-3 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-indigo-900 uppercase">{service.metadata.type} Bag</p>
                        <p className="text-[10px] text-indigo-600 font-medium">Up to {service.metadata.maximum_weight_kg}kg</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-black text-indigo-900">{formatCurrency(service.total_amount, service.total_currency)}</span>
                        <ChevronRight style={{ width: "16px", height: "16px" }} className="text-indigo-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Eco Info */}
          <div className="p-4 bg-green-50 rounded-2xl border border-green-100 flex items-start gap-3">
             <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-green-600 shadow-sm shrink-0">
               <Info style={{ width: "16px", height: "16px" }} />
             </div>
             <div>
               <p className="text-xs font-black text-green-900 uppercase mb-1">Environmental Impact</p>
               <p className="text-[11px] text-green-700 leading-relaxed font-medium">
                 This flight emits <span className="font-bold">{offer.total_emissions_kg}kg of CO₂</span>. This is 12% lower than the route average.
               </p>
             </div>
          </div>
        </div>
      </div>
        <div className="selected_ticket_book_btn_container">
            <div style={{backgroundColor: bookingEngine?.actionButtonsBg, 
                color: bookingEngine?.actionButtonsTxtColor,
                borderRadius: bookingEngine?.actionButtonBorderRadius
            }} 
                onClick={
                    ()=>{book_item_onclick()}
                    /*()=>global.show_start_checkout_page(`${global.stringify_obj_for_template_strings(global.checkout_obj)}`)*/
                } className="selected_ticket_book_btn">
                <i style={{marginRight: 10, color: bookingEngine?.actionButtonsIconColor, fontSize: 19}} className="fa fa-check-square-o" aria-hidden="true"></i>
                Book
            </div>
        </div>
    </div>
  );
};

export default Type4FullFlightDetailsCard;